// DigitalSignatureModal — option B of the approval flow ("Digital signature"):
// shop marks each selected item Approve / Decline, then the customer signs
// directly on the shop's device. We capture the signature with
// react-signature-canvas, upload the PNG to GCS, then hand off to the
// paperAuthorization endpoint which already knows how to create a
// QuoteAuthorization row + apply per-item decisions atomically.
//
// Optional: after the auth lands, also notify the customer via SMS / Email
// (or Skip) with the receipt link.

import { useRef, useState, useEffect } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Group,
  LoadingOverlay,
  Modal,
  ScrollArea,
  SegmentedControl,
  Stack,
  Text,
  TextInput,
} from '@mantine/core';
import { IconAlertCircle, IconCheck, IconX } from '@tabler/icons-react';
import SignatureCanvas from 'react-signature-canvas';
import { quoteApi, storageApi } from '../api/client';
import { getRepairAuthTerms } from '../constants/repairAuthTerms';
import { CaptureDocTypePicker } from '../../components/CaptureDocTypePicker';

type Decision = 'approved' | 'declined';

const BG = '#0C0C0E';
const CARD_BG = '#18181B';
const BORDER = '#27272A';
const TEXT = '#F4F4F5';
const TEXT_SECONDARY = '#A1A1AA';
const TEXT_MUTED = '#71717A';
const GREEN = '#22C55E';
const RED = '#EF4444';
const BLUE = '#2563EB';

type ItemRow = {
  source_id?: string | null;
  label?: string | null;
  amount?: number | string | null;
  default_decision?: 'approve' | 'decline' | 'skip';
};

type Props = {
  opened: boolean;
  onClose: () => void;
  conversationId: number;
  items: ItemRow[];
  customerHasContact?: boolean;
  customerName?: string;
  customerPhone?: string | null;
  customerEmail?: string | null;
  // Shop's teardown reassembly window (days). Null/undefined → default 30.
  // Sourced from shop.report_config.teardown_reassembly_days.
  teardownReassemblyDays?: number | null;
  // Which legal-doc track this capture writes to: 'estimate' (default) or 'invoice'.
  docType?: 'estimate' | 'invoice' | 'both';
  // Choosing the track is the first step inside this pop-up; changing it asks the
  // parent to re-scope the eligible items for the new track.
  onDocTypeChange?: (v: 'estimate' | 'invoice' | 'both') => void;
  onApplied: (opts: { notify: boolean; channel?: 'sms' | 'email' | 'skip'; phone?: string; email?: string }) => void;
};

type SendChannel = 'skip' | 'sms' | 'email';

export function DigitalSignatureModal({
  opened,
  onClose,
  conversationId,
  items,
  customerHasContact = false,
  customerName = '',
  customerPhone = null,
  customerEmail = null,
  teardownReassemblyDays = null,
  docType = 'estimate',
  onDocTypeChange,
  onApplied,
}: Props) {
  const [authorizerName, setAuthorizerName] = useState(customerName || '');
  const [decisions, setDecisions] = useState<Record<string, Decision>>(
    Object.fromEntries(items.map((i) => {
      const intent: Decision = i.default_decision === 'decline' ? 'declined' : 'approved';
      return [String(i.source_id ?? ''), intent];
    })),
  );
  const [hasSignature, setHasSignature] = useState(false);
  const [phone, setPhone] = useState(customerPhone || '');
  const [email, setEmail] = useState(customerEmail || '');
  // Default Skip — writer just captured the signature; explicit opt-in
  // before we blast an SMS/email avoids accidental notifications.
  const [sendChannel, setSendChannel] = useState<SendChannel>('skip');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const sigPadRef = useRef<SignatureCanvas | null>(null);
  // Single-step flow: decisions + the "I agree" checkbox + signature are all
  // captured together; the signature is read straight from the pad on commit.
  const [termsAgreed, setTermsAgreed] = useState(false);
  // Inline T&C preview Modal — opened from the "terms and conditions" link
  // next to the signature pad so customers can read the full document inline.
  const [termsPreviewOpen, setTermsPreviewOpen] = useState(false);

  useEffect(() => {
    if (opened) {
      setAuthorizerName(customerName || '');
      setPhone(customerPhone || '');
      setEmail(customerEmail || '');
      setSendChannel('skip');
      setHasSignature(false);
      setError('');
      setTermsAgreed(false);
      setDecisions(Object.fromEntries(items.map((i) => {
        const intent: Decision = i.default_decision === 'decline' ? 'declined' : 'approved';
        return [String(i.source_id ?? ''), intent];
      })));
    }
  }, [opened, customerName, customerPhone, customerEmail, items]);

  const setDecision = (sid: string, d: Decision) => {
    setDecisions((prev) => ({ ...prev, [sid]: d }));
  };

  // Single-step commit: validate decisions + signature + authorizer name + the
  // agreement checkbox, capture the signature straight from the (still-mounted)
  // pad, then upload + apply atomically. No separate terms page — the agreement
  // is the checkbox next to the pad and the full terms live behind its link.
  const handleSave = async () => {
    setError('');
    const concrete = Object.entries(decisions);
    if (concrete.length === 0) {
      setError('No items to authorize.');
      return;
    }
    const pad = sigPadRef.current;
    if (!pad || pad.isEmpty()) {
      setError('Please capture the customer signature first.');
      return;
    }
    if (!authorizerName.trim()) {
      setError('Authorizer name is required.');
      return;
    }
    if (!termsAgreed) {
      setError('Please confirm the customer agrees to the terms.');
      return;
    }
    if (sendChannel === 'sms' && !phone.trim()) {
      setError('Enter a phone number or pick Skip.');
      return;
    }
    if (sendChannel === 'email' && !email.trim()) {
      setError('Enter an email address or pick Skip.');
      return;
    }

    setSaving(true);
    try {
      // 1) Capture the signature dataURL from the pad (mounted in this step).
      let dataUrl = '';
      try {
        dataUrl = (pad as any).getTrimmedCanvas().toDataURL('image/png');
      } catch {
        dataUrl = pad.toDataURL('image/png');
      }
      // 2) Convert dataURL → File → upload via storage presigned URL so the
      //    backend can reference it as a real GCS blob (same as the paper
      //    flow). Re-using the upload path keeps blob lifecycle uniform.
      const blob = await (await fetch(dataUrl)).blob();
      const file = new File([blob], `digital-signature-${Date.now()}.png`, { type: 'image/png' });
      const { blob_name } = await storageApi.uploadToPresignedUrl(file);
      // 3) Hand off to paperAuthorization — it creates the QuoteAuthorization
      //    row + applies decisions atomically. Method is implicitly 'paper_photo'
      //    on the backend; semantically this is digital, but the storage shape
      //    is identical (a signature image blob).
      const payload: Record<string, 'approved' | 'declined'> = {};
      for (const [sid, d] of concrete) payload[sid] = d as 'approved' | 'declined';
      // "Both" records the same signature against both tracks. Reusing one blob
      // across the two applies is allowed (replay-protection only blocks reuse
      // across *different* quotes).
      const tracks = docType === 'both' ? (['estimate', 'invoice'] as const) : [docType];
      for (const t of tracks) {
        try {
          const result = await quoteApi.paperAuthorization(
            conversationId,
            blob_name,
            payload,
            authorizerName.trim() || undefined,
            t,
          );
          if (result.rejected?.length) {
            setError(`Some items were rejected: ${result.rejected.join('; ')}`);
            setSaving(false);
            return;
          }
        } catch (err) {
          // 409 = this track already has this signature on file (e.g. retrying
          // "Both" after a partial failure committed the estimate track). Treat
          // as already-applied and continue to the next track.
          if ((err as { response?: { status?: number } })?.response?.status === 409) continue;
          throw err;
        }
      }
      onApplied({
        notify: sendChannel !== 'skip',
        channel: sendChannel,
        phone: phone.trim(),
        email: email.trim(),
      });
      handleClose();
    } catch (e: unknown) {
      const detail = (e as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Failed to record signature.');
      setSaving(false);
    }
  };

  const handleClose = () => {
    sigPadRef.current?.clear();
    setHasSignature(false);
    setAuthorizerName(customerName || '');
    setPhone(customerPhone || '');
    setEmail(customerEmail || '');
    setSendChannel('skip');
    setError('');
    setSaving(false);
    setTermsAgreed(false);
    onClose();
  };

  const approvedCount = Object.values(decisions).filter((d) => d === 'approved').length;
  const declinedCount = Object.values(decisions).filter((d) => d === 'declined').length;
  const approvedTotal = items.reduce(
    (sum, it) => sum + (decisions[String(it.source_id ?? '')] === 'approved' ? Number(it.amount || 0) : 0),
    0,
  );
  const declinedTotal = items.reduce(
    (sum, it) => sum + (decisions[String(it.source_id ?? '')] === 'declined' ? Number(it.amount || 0) : 0),
    0,
  );

  return (
    <Modal
      opened={opened}
      onClose={saving ? () => {} : handleClose}
      withCloseButton={!saving}
      size="lg"
      title={<Text size="sm" fw={600} c={TEXT}>Digital signature — customer signs on this device</Text>}
      styles={{
        content: { background: CARD_BG, border: `1px solid ${BORDER}` },
        header: { background: CARD_BG },
        overlay: { backdropFilter: 'blur(4px)' },
      }}
      radius="md"
    >
      <Box pos="relative">
        <LoadingOverlay visible={saving} overlayProps={{ blur: 1, backgroundOpacity: 0.4 }} />
        <Stack gap={14}>
          {error && (
            <Alert color="red" icon={<IconAlertCircle size={15} />} radius="sm" styles={{ message: { fontSize: 13 } }}>
              {error}
            </Alert>
          )}

          {onDocTypeChange && (
            <CaptureDocTypePicker value={docType} onChange={onDocTypeChange} hint="What the signature authorizes" />
          )}
          <TextInput
            label="Authorizer name"
            required
            value={authorizerName}
            onChange={(e) => setAuthorizerName(e.currentTarget.value)}
            placeholder="Customer's full name"
            styles={{
              input: { background: BG, borderColor: BORDER, color: TEXT },
              label: { color: TEXT_SECONDARY, marginBottom: 4, fontSize: 13 },
            }}
          />

          <Box>
            <Group justify="space-between" mb={6}>
              <Text size="xs" c={TEXT_MUTED}>Per-item decision</Text>
              <Text size="xs" c={TEXT_MUTED}>
                <Text span c={GREEN} fw={600}>{approvedCount}</Text> approved
                <Text span c={TEXT_MUTED} ml={4}>(${approvedTotal.toFixed(2)})</Text>
                <Text span c={TEXT_MUTED} ml={4}>·</Text>
                <Text span c={RED} fw={600} ml={4}>{declinedCount}</Text> declined
                <Text span c={TEXT_MUTED} ml={4}>(${declinedTotal.toFixed(2)})</Text>
              </Text>
            </Group>
            <ScrollArea.Autosize mah={220}>
              <Stack gap={6}>
                {items.map((it) => {
                  const sid = String(it.source_id ?? '');
                  const d = decisions[sid] ?? 'approved';
                  return (
                    <Box key={sid} style={{
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                      gap: 10, padding: '10px 12px',
                      background: BG, border: `1px solid ${BORDER}`, borderRadius: 8,
                    }}>
                      <Box style={{ flex: 1, minWidth: 0 }}>
                        <Text size="sm" c={TEXT} fw={500} truncate>{it.label || '(no label)'}</Text>
                        <Text size="xs" c={TEXT_MUTED}>${Number(it.amount || 0).toFixed(2)}</Text>
                      </Box>
                      <Group gap={4} style={{ flexShrink: 0 }}>
                        <DecisionBtn label="Approve" icon={<IconCheck size={12} />} color={GREEN} active={d === 'approved'} onClick={() => setDecision(sid, 'approved')} />
                        <DecisionBtn label="Decline" icon={<IconX size={12} />} color={RED} active={d === 'declined'} onClick={() => setDecision(sid, 'declined')} />
                      </Group>
                    </Box>
                  );
                })}
              </Stack>
            </ScrollArea.Autosize>
          </Box>

          {/* ── Step 1: customer signature ─────────────────────────── */}
          <Box>
            <Group justify="space-between" mb={6}>
              <Text size="xs" c={TEXT_MUTED} tt="uppercase" fw={600}>
                Customer signature
              </Text>
              <Text size="xs" c={TEXT} fw={600}>
                Total to authorize: ${approvedTotal.toFixed(2)}
              </Text>
            </Group>
            {/* Terms shown ABOVE the canvas so the customer reads them
                before signing. Plain English, no shop-specific legalese —
                shops can amend via Settings → Report config in the future. */}
            <Box style={{
              padding: '8px 10px', borderRadius: 6,
              background: 'rgba(99,102,241,0.06)', border: `1px solid ${BORDER}`,
              marginBottom: 8,
            }}>
              <Text size="sm" c={TEXT_SECONDARY} lh={1.5}>
                By signing below, I, <Text span c={TEXT} fw={600}>{authorizerName || '___'}</Text>,
                authorize the shop to perform the {approvedCount} approved
                item{approvedCount === 1 ? '' : 's'} above for a total of{' '}
                <Text span c={TEXT} fw={600}>${approvedTotal.toFixed(2)}</Text>{' '}
                (excluding tax). I understand that declined items will not be
                performed and agree to the{' '}
                <Text
                  span
                  c="#A5B4FC"
                  style={{ textDecoration: 'underline', cursor: 'pointer' }}
                  onClick={() => setTermsPreviewOpen(true)}
                  role="button"
                >
                  terms and conditions
                </Text>.
                Additional work may require my separate authorization.
              </Text>
            </Box>
            <Checkbox
              checked={termsAgreed}
              onChange={(e) => setTermsAgreed(e.currentTarget.checked)}
              mb={10}
              label={
                <Text size="sm" c={TEXT}>
                  I, {authorizerName || '___'}, have read and agree to the terms above.
                </Text>
              }
              styles={{ input: { cursor: 'pointer' }, label: { cursor: 'pointer' } }}
            />
            <Group justify="space-between" mb={6}>
              <Text size="xs" c={TEXT_MUTED}>Sign in the box</Text>
              <Button
                size="compact-xs"
                variant="subtle"
                color="gray"
                disabled={!hasSignature}
                onClick={() => { sigPadRef.current?.clear(); setHasSignature(false); }}
              >
                Clear
              </Button>
            </Group>
            <Box style={{
              background: '#fff',
              border: `1px solid ${hasSignature ? GREEN : BORDER}`,
              borderRadius: 8,
              overflow: 'hidden',
              touchAction: 'none',
            }}>
              <SignatureCanvas
                ref={(r) => { sigPadRef.current = r; }}
                penColor="#0f172a"
                canvasProps={{ style: { width: '100%', height: 160, display: 'block', background: '#fff' } }}
                onEnd={() => setHasSignature(!(sigPadRef.current?.isEmpty()))}
              />
            </Box>
            <Text size="xs" c={TEXT_MUTED} mt={4}>Sign with finger or stylus.</Text>
          </Box>

          {/* ── Step 2: send the customer a receipt link ─────────────
              Only shown after the signature is captured, so the writer
              sees a clean two-step flow instead of all toggles at once. */}
          {customerHasContact && hasSignature && (
            <Box>
              <Text size="xs" c={TEXT_MUTED} tt="uppercase" fw={600} mb={6}>
                Send customer a receipt link
              </Text>
              <SegmentedControl
                fullWidth
                size="xs"
                value={sendChannel}
                onChange={(v) => setSendChannel(v as SendChannel)}
                data={[
                  { label: 'Skip', value: 'skip' },
                  { label: '📲 SMS', value: 'sms' },
                  { label: '✉️ Email', value: 'email' },
                ]}
                styles={{
                  root: { background: BG, border: `1px solid ${BORDER}` },
                  indicator: { background: BLUE },
                  label: { color: TEXT },
                }}
              />
              {sendChannel === 'sms' && (
                <TextInput
                  mt={8}
                  label="Phone"
                  value={phone}
                  onChange={(e) => setPhone(e.currentTarget.value)}
                  placeholder="+1 555 123 4567"
                  styles={{
                    input: { background: BG, borderColor: BORDER, color: TEXT },
                    label: { color: TEXT_SECONDARY, fontSize: 12 },
                  }}
                />
              )}
              {sendChannel === 'email' && (
                <TextInput
                  mt={8}
                  label="Email"
                  value={email}
                  onChange={(e) => setEmail(e.currentTarget.value)}
                  placeholder="customer@example.com"
                  styles={{
                    input: { background: BG, borderColor: BORDER, color: TEXT },
                    label: { color: TEXT_SECONDARY, fontSize: 12 },
                  }}
                />
              )}
            </Box>
          )}

          <Group justify="space-between" mt={4}>
            <Button size="sm" variant="subtle" color="gray" onClick={handleClose} disabled={saving}>
              Cancel
            </Button>
            <Button
              size="sm"
              color="haist"
              onClick={handleSave}
              loading={saving}
              disabled={!hasSignature || !authorizerName.trim() || !termsAgreed}
            >
              Send final
            </Button>
          </Group>
        </Stack>
      </Box>

      {/* Inline T&C preview — opened from the "terms and conditions" link
          in the pre-sign mini-summary. Does not advance the wizard or
          commit anything; pure read-only preview. */}
      <Modal
        opened={termsPreviewOpen}
        onClose={() => setTermsPreviewOpen(false)}
        size="lg"
        title={<Text size="sm" fw={600} c={TEXT}>Terms and conditions</Text>}
        styles={{
          content: { background: CARD_BG, border: `1px solid ${BORDER}` },
          header: { background: CARD_BG },
          overlay: { backdropFilter: 'blur(4px)' },
        }}
        radius="md"
      >
        <ScrollArea.Autosize mah={420} style={{
          background: BG, border: `1px solid ${BORDER}`, borderRadius: 8, padding: 12,
        }}>
          <Text size="sm" c={TEXT_SECONDARY} lh={1.55} style={{ whiteSpace: 'pre-wrap' }}>
            {getRepairAuthTerms({ teardownDays: teardownReassemblyDays })}
          </Text>
        </ScrollArea.Autosize>
        <Group justify="flex-end" mt={12}>
          <Button size="sm" variant="subtle" color="gray" onClick={() => setTermsPreviewOpen(false)}>Close</Button>
        </Group>
      </Modal>
    </Modal>
  );
}

function DecisionBtn({
  label, icon, color, active, onClick,
}: {
  label: string;
  icon?: React.ReactNode;
  color: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <Button
      size="compact-xs"
      variant={active ? 'filled' : 'outline'}
      onClick={onClick}
      leftSection={icon}
      styles={{
        root: {
          background: active ? color : 'transparent',
          borderColor: color,
          color: active ? '#fff' : color,
          minWidth: 64,
          height: 26,
        },
      }}
    >
      {label}
    </Button>
  );
}
